import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import Alert from "@mui/material/Alert";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import useMediaQuery from "@mui/material/useMediaQuery";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import ConversationPane, {
  ChatMessage,
} from "../../common/components/chat/ConversationPane";
import InterviewService, {
  InterviewOutcome,
} from "../../services/InterviewService";
import InterviewState, {
  GapPriority,
  InterviewAnswer,
  InterviewCamera2Detection,
  InterviewDetection,
  InterviewGap,
  InterviewItem,
  InterviewOp,
  InterviewSkill,
  InterviewStep,
} from "../../types/Interview";
import alteracoesDoRascunho from "../alteracoesDoRascunho";
import publicarAplicados from "../publicarAplicados";
import RespostaGuiada from "./RespostaGuiada";
import CampoEditavel from "./CampoEditavel";
import RegrasDoItem from "./RegrasDoItem";
import AbaRegrasCamera2 from "./AbaRegrasCamera2";
import CabecalhoSecaoRegras from "./CabecalhoSecaoRegras";
import AbaCenario, { camposVaziosDoCenario } from "./AbaCenario";
import PainelLacunas, { abaDaLacuna } from "./PainelLacunas";
import PainelAprofundar from "./PainelAprofundar";
import PassosDoItem from "./PassosDoItem";
import SobreposicaoTrabalho from "./SobreposicaoTrabalho";
import mensagemArquivos, { renderTextoComNegrito } from "./MensagemArquivos";
import ImportacaoDocumentos, { AnexarArquivos, importacaoAguardaLeitura, importacaoPendente, useImportacaoDocumentos } from "./ImportacaoDocumentos";
import palette from "../../common/styles/palette";
import { avaliarDeteccao, ROTULOS, rotuloContagemDeteccao } from "./detectabilidade";
import type { Deteccao } from "./detectabilidade";
import { envir } from "../../scenarios/const-values";

const COR_NIVEL = {
  forte: palette.emeraldDeep,
  revisar: palette.warning,
  fraca: palette.dangerText,
} as const;

// A tradução de prioridade e as cores dela moram em `PainelLacunas`, que é
// quem as desenha. Havia uma cópia aqui enquanto o painel era inline; duas
// tabelas para o mesmo domínio fechado é uma a mais do que existe.

// `scenario.environment` é int 1..6. O catálogo é o do formulário manual e é
// IMPORTADO, não recopiado: uma segunda tabela de rótulos aqui já divergiu uma
// vez (eu havia escrito 2 = "Enfermaria", quando 2 é "pronto socorro" e
// enfermaria é 3 — "unidade de internação"). Duas fontes para o mesmo domínio
// fechado é uma fonte a mais do que existe.

type ItemComSecao = InterviewItem & {
  numero: number;
  skillRef: string;
  skillName: string;
  deteccao: Deteccao;
};
type GrupoResumoDeteccao = "forte" | "revisar";

/** Enunciado de referência para a frase que a edição manual manda ao fio. */
const curto = (texto: string) =>
  texto.length > 140 ? texto.slice(0, 140) + "…" : texto;

const pertenceAoChecklist = (gap: InterviewGap) =>
  ["checklist", "skill", "item"].some((prefixo) =>
    gap.caminho.startsWith(prefixo)
  );

const somenteSaudacao = (texto: string) =>
  /^(oi|ola|bom dia|boa tarde|boa noite|tudo bem)[!,.?\s]*$/.test(
    texto
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
  );

const pedePropostaCompleta = (texto: string) =>
  /^(pode\s+)?(propor|completar|montar|gerar)(\s+(o|a|uma|os|as|restante|detalhes|cenario).*)?[!,.?\s]*$/.test(
    texto
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
  );

const PEDIDO_CHECKLIST =
  "Crie o Checklist de Avaliação completo a partir do Cenário revisado.";
const FOCO_ENQUADRAMENTO = "CAMERA2_GUIDANCE";
const focoRegras = (ref: string, camera2 = false) => `${camera2 ? "ITEM_RULES_CAMERA2" : "ITEM_RULES"}:${ref}`;
const ehFocoRegras = (focus?: string | null) => Boolean(focus?.startsWith("ITEM_RULES:") || focus?.startsWith("ITEM_RULES_CAMERA2:"));

/**
 * Abas endereçáveis pela URL (`?aba=checklist`). Só as que outra tela tem
 * motivo para pedir: a lista de Checklists manda o professor direto para a
 * aba do Checklist que aquele Background IA gerou. Cenário é o padrão e não
 * precisa de nome; Regras e Conversa ninguém abre de fora.
 */
const ABA_POR_NOME: Record<string, number> = { checklist: 1 };

/**
 * O que está sendo enviado ao modelo neste instante: contagem de seções e
 * itens do Rascunho em edição, mais o tamanho serializado. Alimenta a
 * referência ao processo no painel Aprofundar — o professor vê a chamada,
 * não só o resultado.
 */
const resumoDoRascunho = (
  state: InterviewState | null
): { sections: number; items: number; chars: number } | null => {
  if (!state?.draft?.checklist?.skills) return null;
  const skills = state.draft.checklist.skills;
  const items = skills.reduce((n, s) => n + (s.items?.length ?? 0), 0);
  return {
    sections: skills.length,
    items,
    chars: JSON.stringify(state.draft).length,
  };
};

/**
 * A Entrevista — a tela, não um painel do formulário.
 *
 * Ela existe ANTES do Checklist, porque é ela que o cria: nenhum id de
 * checklist é exigido para começar. O painel revertido morava dentro de
 * `/checklist/form` e obrigava o professor a salvar um checklist vazio antes
 * de poder conversar; foi assim que nasceram as quatro cascas 66–69.
 *
 * Quatro abas, na sequência de autoria:
 *
 *   Cenário       — premissas e campos revisáveis antes da confirmação.
 *   Checklist     — seções, Itens e pesos da correção oficial.
 *   Regras da IA  — as cinco facetas com que o agente julga cada Item.
 *   Conversa      — histórico e Lacunas da fase corrente.
 *
 * O componente é renderizador puro do envelope: recebe `InterviewState` e
 * desenha `messages`, `draft`, `gaps` e `next_question`. Não guarda cópia do
 * Rascunho, não aplica patch e não conta Lacuna — quem faz isso é o servidor,
 * que é o único lado que pode barrar a escrita. Era aí que o painel revertido
 * errava, com `msgs`, `proposal`, `iniciado` e `aba` divergindo entre si.
 */
export default function Interview() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Abaixo de 1200px não sobra largura para a coluna de 320px do painel sem
  // espremer a conversa — que é o problema que a coluna existe para resolver.
  // Aí ele volta para cima, como acordeão recolhido. O limiar é o mesmo em que
  // o template já solta a margem do menu (`index.styles.ts`, breakpoint 900–1200).
  const estreito = !useMediaQuery("(min-width:1200px)");

  const [state, setState] = useState<InterviewState | null>(null);
  const [input, setInput] = useState("");
  const [turnBusy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [publicado, setPublicado] = useState(false);
  // A aba pedida na URL vale só na entrada: se o Cenário ainda não foi revisado
  // o efeito de invalidação abaixo devolve para o Cenário, como para qualquer
  // outra forma de chegar a uma aba travada.
  const [aba, setAba] = useState(
    ABA_POR_NOME[searchParams.get("aba") ?? ""] ?? 0
  );
  // A Lacuna que o professor escolheu no painel. `null` = seguir a corrente,
  // que é o comportamento de sempre. Guardo o objeto e não o código porque a
  // identidade de uma Lacuna é `codigo` + `ref` (a mesma D01 existe para cada
  // Item), e porque o efeito colateral disto é só desenhar — a autoridade
  // sobre o que falta continua sendo do servidor, a cada turno.
  const [lacunaFocada, setLacunaFocada] = useState<InterviewGap | null>(null);
  const [mensagemAoFocar, setMensagemAoFocar] = useState<{ seq: number | null } | null>(null);
  const [campoDestino, setCampoDestino] = useState<{ id: string; caminho: string; pedido: number; contornoCompleto: boolean } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const abrindo = useRef(false);
  // A geração tem estado próprio ao lado de `busy` porque a espera é de outra
  // ordem de grandeza: `busy` dura o tempo de um turno de texto, `gerando`
  // dura dezenas de segundos e precisa de explicação na tela.
  const [gerando, setGerando] = useState(false);

  // O Aprofundamento tem estado próprio pelo mesmo motivo da geração: a
  // varredura dura de um a dois minutos. `adotando` é separado de `carregando`
  // porque as duas esperas dizem coisas diferentes ao professor — uma é «estou
  // lendo o seu documento», a outra é «estou gravando o que você marcou».
  const [painelAprofundar, setPainelAprofundar] = useState(false);
  const [aprofundando, setAprofundando] = useState(false);
  const [sincronizandoGpu, setSincronizandoGpu] = useState(false);
  const [adotando, setAdotando] = useState(false);
  // Um pedido focado pode voltar vazio e o professor então acrescentar uma
  // ênfase no próprio painel. Preserve o Item nesse segundo pedido; sem isso,
  // a retentativa silenciosamente virava uma varredura do documento inteiro.
  const [itemRefAprofundar, setItemRefAprofundar] = useState<string | null>(
    null
  );
  const [itemNomeAprofundar, setItemNomeAprofundar] = useState<string | null>(null);
  const [camera2Aprofundar, setCamera2Aprofundar] = useState(false);
  const [enquadramentoAprofundar, setEnquadramentoAprofundar] = useState(false);
  const [copilotoAberto, setCopilotoAberto] = useState(!estreito);
  useEffect(() => {
    if (estreito) setCopilotoAberto(false);
  }, [estreito]);
  useEffect(() => {
    if (state?.enrichment && state.enrichment.amendments?.length > 0) {
      setErro((atual) =>
        atual && (atual.includes("aprofundamento") || atual.includes("MedGemma"))
          ? null
          : atual
      );
    }
  }, [state?.enrichment]);

  // O dock do Copiloto flutua sobre a página (`position: fixed`), e um botão
  // de ação embaixo dele é um botão que não existe: o professor não vê e não
  // clica. Em vez de chutar a largura do dock, MEÇO a dele e reservo o vão
  // correspondente à direita dos rodapés de ação. Em jsdom não há layout
  // (`offsetWidth` é 0), o vão fica em zero e nenhum teste depende da medida.
  const dockCopiloto = useRef<HTMLDivElement | null>(null);
  const [larguraDock, setLarguraDock] = useState(0);
  useEffect(() => {
    const el = dockCopiloto.current;
    if (!el) {
      setLarguraDock(0);
      return;
    }
    const medir = () => setLarguraDock(el.offsetWidth);
    medir();
    if (typeof ResizeObserver === "undefined") return;
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, [aba, copilotoAberto, estreito]);
  const [novasAlteracoesCenario, setNovasAlteracoesCenario] = useState(0);
  const [novasAlteracoesChecklist, setNovasAlteracoesChecklist] = useState(0);
  const [novasAlteracoesRegras, setNovasAlteracoesRegras] = useState(0);
  const [novasAlteracoesRegrasCamera2, setNovasAlteracoesRegrasCamera2] = useState(0);
  const [resumoDeteccoesAberto, setResumoDeteccoesAberto] =
    useState<GrupoResumoDeteccao | null>(null);
  const [secoesRegrasAbertas, setSecoesRegrasAbertas] = useState<
    Record<string, boolean>
  >({});
  const [itemRegraDestacado, setItemRegraDestacado] = useState<string | null>(
    null
  );
  // As Etapas de quais Itens estão abertas na aba do Checklist (`steps[]` da
  // ADR-0003). Ficam recolhidas por padrão porque a aba do Checklist é a visão
  // de estrutura — o que ela precisava era o SINAL de que as Etapas existem,
  // não a lista inteira aberta em doze Itens de uma vez.
  const [etapasAbertas, setEtapasAbertas] = useState<Record<string, boolean>>(
    {}
  );

  /** Navegação única: entrar numa aba significa que as novidades foram vistas. */
  const abrirAba = (destino: number) => {
    setAba(destino);
    if (destino === 0) setNovasAlteracoesCenario(0);
    if (destino === 1) setNovasAlteracoesChecklist(0);
    if (destino === 2) setNovasAlteracoesRegras(0);
    if (destino === 4) setNovasAlteracoesRegrasCamera2(0);
  };
  const [versaoCamposCamera2, setVersaoCamposCamera2] = useState(0);
  const [secoesRegrasCamera2Abertas, setSecoesRegrasCamera2Abertas] = useState<Record<string, boolean>>({});
  // Um único ponto de pouso para as cinco operações: `ok` redesenha, `conflict`
  // redesenha com o estado do servidor e avisa, `failed` NUNCA vira sucesso.
  // Turnos feitos pela Conversa — texto livre ou resposta guiada — atualizam
  // os contadores. Eles representam a ÚLTIMA resposta, não um total acumulado.
  const receber = (
    r: InterviewOutcome,
    sucesso?: (s: InterviewState) => void,
    notificarAlteracoes = false
  ) => {
    if (r.kind === "ok") {
      if (notificarAlteracoes && state) {
        const alteracoes =
          r.state.revision > state.revision
            ? alteracoesDoRascunho(state.draft, r.state.draft)
            : { cenario: 0, checklist: 0, regras: 0, regrasCamera2: 0 };
        setNovasAlteracoesCenario(aba === 0 ? 0 : alteracoes.cenario);
        setNovasAlteracoesChecklist(aba === 1 ? 0 : alteracoes.checklist);
        setNovasAlteracoesRegras(aba === 2 ? 0 : alteracoes.regras);
        setNovasAlteracoesRegrasCamera2(aba === 4 ? 0 : alteracoes.regrasCamera2);
      }
      setState(r.state);
      setErro(null);
      sucesso?.(r.state);
      return;
    }
    if (r.kind === "conflict") {
      setState(r.state);
      setErro(r.msg);
      toast(r.msg, { type: "warning" });
      return;
    }
    setErro(r.msg);
    toast(r.msg, { type: "error" });
  };

  const importacao = useImportacaoDocumentos(state, receber, turnBusy || applying);
  const busy = turnBusy || importacao.uploading || importacao.acting;
  const erroImportacaoNotificado = useRef<string | null>(null);
  useEffect(() => {
    const error = state?.document_import_error;
    const key = error ? `${state?.interview_id}:${error.id}` : null;
    if (error && key !== erroImportacaoNotificado.current) {
      erroImportacaoNotificado.current = key;
      toast(error.message, { type: "error" });
    }
  }, [state?.interview_id, state?.document_import_error]);

  useEffect(() => {
    let vivo = true;
    const entrar = async () => {
      setCarregando(true);
      if (id) {
        const r = await InterviewService.show(id);
        if (!vivo) return;
        receber(r);
        setCarregando(false);
        return;
      }
      // Sem id na URL: a tela abre a Entrevista. Não há checklist para exigir.
      if (abrindo.current) return;
      abrindo.current = true;
      const r = await InterviewService.open();
      if (!vivo) return;
      if (r.kind === "ok") {
        navigate(`/interview/detail/${r.state.interview_id}`, {
          replace: true,
        });
        return;
      }
      receber(r);
      setCarregando(false);
    };
    entrar();
    return () => {
      vivo = false;
    };
  }, [id]);

  // As abas do Checklist/Regras só ficam trancadas enquanto NÃO EXISTE
  // Checklist: é a mesma barreira do servidor (a primeira escrita no Checklist
  // exige o Cenário confirmado). Depois que o Checklist existe, uma edição ou
  // Emenda adotada no Cenário derruba a confirmação — e o professor precisa
  // reconfirmar antes de APLICAR — mas trancar as abas aqui escondia
  // justamente o que ele acabou de adotar: «adotei e não apareceu» (MEDIDO).
  const abasDoChecklistTrancadas = Boolean(
    state && !state.scenario_reviewed && state.draft.checklist.skills.length === 0
  );

  useEffect(() => {
    // Enquanto o envelope não chegou não há o que invalidar — e zerar aqui
    // engoliria a aba pedida pela URL antes de a tela saber se ela é permitida.
    if (abasDoChecklistTrancadas && (aba === 1 || aba === 2 || aba === 4)) {
      setAba(0);
      setNovasAlteracoesCenario(0);
    }
  }, [abasDoChecklistTrancadas, aba]);

  const itens: ItemComSecao[] = useMemo(
    () =>
      (state?.draft.checklist.skills || [])
        .flatMap((skill) =>
          skill.items.map((item) => ({
            ...item,
            skillRef: skill.ref,
            skillName: skill.name,
            deteccao: avaliarDeteccao(item),
          }))
        )
        .map((item, idx) => ({ ...item, numero: idx + 1 })),
    [state]
  );
  const numeroPorItemRef = useMemo(
    () =>
      Object.fromEntries(itens.map((item) => [item.ref, item.numero])) as Record<
        string,
        number
      >,
    [itens]
  );
  const rotuloItem = (item: InterviewItem) => {
    const num = numeroPorItemRef[item.ref];
    const enunciado = curto(item.question);
    return num ? `Item ${num} (**${enunciado}**)` : `**${enunciado}**`;
  };
  useEffect(() => {
    setResumoDeteccoesAberto(null);
    setSecoesRegrasAbertas({});
    setSecoesRegrasCamera2Abertas({});
    setItemRegraDestacado(null);
  }, [id]);

  useEffect(() => {
    if (aba !== 2 || !itemRegraDestacado) return;

    const timer = window.setTimeout(() => {
      const alvo = document.getElementById(
        `interview-rule-item-${itemRegraDestacado}`
      );
      alvo?.scrollIntoView?.({ behavior: "auto", block: "start" });
      alvo?.focus({ preventScroll: true });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [aba, itemRegraDestacado, secoesRegrasAbertas]);

  const navegarParaRegra = (skillRef: string, itemRef: string) => {
    const secoesEmFoco: Record<string, boolean> = {};
    state?.draft.checklist.skills.forEach((skill) => {
      secoesEmFoco[skill.ref] = skill.ref === skillRef;
    });
    setSecoesRegrasAbertas(secoesEmFoco);
    setItemRegraDestacado(itemRef);
    setResumoDeteccoesAberto(null);
  };

  const itensPorGrupo = useMemo(() => {
    const forte: ItemComSecao[] = [];
    const revisar: ItemComSecao[] = [];
    itens.forEach((item) =>
      (item.deteccao.nivel === "forte" ? forte : revisar).push(item)
    );
    return { forte, revisar };
  }, [itens]);
  const itensPorSecao = useMemo(() => {
    const porSecao: Record<string, ItemComSecao[]> = {};
    itens.forEach((item) => {
      (porSecao[item.skillRef] ||= []).push(item);
    });
    return porSecao;
  }, [itens]);

  const nRevisar = itensPorGrupo.revisar.length;
  const nCriticos = itens.filter((item) => item.critical).length;
  const aplicada = state?.status === "aplicada" && !!state.applied;

  const publicar = async () => {
    if (!state?.applied || publicando) return;
    setPublicando(true);
    try {
      await publicarAplicados(state.applied);
      setPublicado(true);
      toast(t("interview_published"), { type: "success" });
    } catch (e: any) {
      toast(e?.response?.data?.Msg || t("interview_publish_error"), {
        type: "error",
      });
    } finally {
      setPublicando(false);
    }
  };
  const vaziosCenario = state ? camposVaziosDoCenario(state.draft) : 0;
  const checklistVazio = (state?.draft.checklist.skills.length || 0) === 0;
  // A aba do Checklist vazia já mostra «Gerar Checklist com IA» em botão
  // grande. Quando ela está aberta, o chip do Copiloto seria o MESMO botão
  // duas vezes na mesma tela, a um palmo de distância.
  const acaoDeGerarVisivelNaAba =
    aba === 1 && itens.length === 0 && checklistVazio;
  const cenarioSemRevisao = Boolean(state && !state.scenario_reviewed);
  const aguardandoConfirmacao = cenarioSemRevisao && vaziosCenario === 0;
  const gapsDaFase = (state?.gaps || []).filter(
    (gap) => !cenarioSemRevisao || !pertenceAoChecklist(gap)
  );
  const bloqueantes = (state?.gaps || []).filter((g) => g.prioridade === "P0");

  // Há o que aprofundar quando existe texto para densificar ou Item para
  // regrar. Um Rascunho recém-aberto tem nome vazio e zero seções: ali o
  // caminho é gerar, não aprofundar.
  const temRascunho = Boolean(
    state &&
      (itens.length > 0 ||
        state.draft.scenario.description_scene.trim() !== "" ||
        state.draft.scenario.goal_scene.trim() !== "")
  );
  const revisaoMedicaEmAndamento =
    itemRefAprofundar === null && !enquadramentoAprofundar && (aprofundando || sincronizandoGpu);
  const bloquearRevisaoMedica =
    !temRascunho || (busy && !revisaoMedicaEmAndamento);

  const briefingDaConversa = useMemo(
    () =>
      (state?.messages || [])
        .filter((message) => message.role === "professor")
        .map((message) => message.text.trim())
        .filter(Boolean)
        .slice(-12)
        .join("\n"),
    [state]
  );

  const temContextoParaGerar = Boolean(
    state &&
      (temRascunho ||
        state.draft.name.trim() !== "" ||
        state.draft.theme_id !== null ||
        state.draft.competence_id !== null ||
        briefingDaConversa !== "")
  );

  // Contexto levantado pela conversa. Só entra o que já existe no Rascunho.
  const contexto = useMemo(() => {
    const c = state?.draft.scenario;
    if (!c) return [];
    return [
      c.complexity ? `complexidade ${c.complexity}` : undefined,
      c.environment
        ? envir.find((e) => e.value === c.environment)?.label
        : undefined,
      c.sex ? `sexo ${c.sex}` : undefined,
      c.age ? `${c.age} anos` : undefined,
      c.time_duration || undefined,
    ].filter((v): v is string => Boolean(v));
  }, [state]);



  /**
   * Primeira fase: a conversa é o briefing. A geração preenche somente o
   * Cenário; não existe um segundo campo para o professor repetir o caso.
   */
  const gerarCenario = async (briefing: string, mensagem: string) => {
    if (!state || busy || gerando || importacaoAguardaLeitura(state)) return;
    setGerando(true);
    setBusy(true);
    try {
      receber(
        await InterviewService.generate(
          state.interview_id,
          mensagem,
          state.revision,
          briefing,
          "scenario"
        ),
        undefined,
        true
      );
    } finally {
      setBusy(false);
      setGerando(false);
    }
  };

  const gerarCenarioDoContexto = () => {
    if (!state || busy || gerando || importacaoAguardaLeitura(state)) return;
    if (!temContextoParaGerar) {
      setCopilotoAberto(true);
      toast(t("interview_generate_needs_briefing"), { type: "info" });
      return;
    }

    void gerarCenario(
      briefingDaConversa ||
        "Complete uma primeira versão coerente a partir dos campos já preenchidos.",
      "Use nossa conversa para completar uma primeira versão do Cenário."
    );
  };

  const enviar = async () => {
    const message = input.trim();
    if (busy || !state) return;
    if (importacao.files.length > 0) {
      if (await importacao.upload(message)) setInput("");
      return;
    }
    if (!message) return;
    setInput("");

    const primeiroBriefing =
      !temRascunho &&
      !state.scenario_reviewed &&
      checklistVazio &&
      !state.messages.some((item) => item.role === "professor") &&
      !somenteSaudacao(message);
    const completarAPedido =
      !state.scenario_reviewed &&
      temContextoParaGerar &&
      pedePropostaCompleta(message);

    if (primeiroBriefing || completarAPedido) {
      const contexto = [briefingDaConversa, message].filter(Boolean).join("\n");
      await gerarCenario(contexto, message);
      return;
    }

    setBusy(true);
    try {
      receber(
        emFoco
          ? await InterviewService.turn(state.interview_id, message, state.revision,
            undefined, undefined, { codigo: emFoco.codigo, ref: emFoco.ref })
          : await InterviewService.turn(state.interview_id, message, state.revision),
        undefined,
        true
      );
    } finally {
      setBusy(false);
    }
  };

  // Responder por componente é o MESMO turno — só que a resposta já vai
  // estruturada, e o servidor a aplica sem consultar o modelo. `rotulo` é o
  // que entra na conversa como fala do professor, para o fio ficar legível.
  const responderGuiado = async (rotulo: string, answer: InterviewAnswer) => {
    if (!state || busy) return;
    setBusy(true);
    receber(
      await InterviewService.turn(
        state.interview_id,
        rotulo,
        state.revision,
        answer
      ),
      undefined,
      true
    );
    setBusy(false);
  };

  /**
   * Edição manual nas abas. Mesmo `turn`, mesmas operações da lista branca —
   * é o terceiro produtor do mesmo patch, ao lado do modelo e do componente
   * de resposta.
   *
   * Vira turno na conversa DE PROPÓSITO: o fio continua sendo a história única
   * da autoria, I11 continua valendo (a fala seguinte termina em pergunta), e
   * a Lacuna recomputa sozinha — mexeu no peso e a soma deixou de dar 100, ela
   * reaparece no mesmo instante. Deixar a edição por fora da conversa criaria
   * duas histórias que divergem.
   */
  const editar = async (mensagem: string, ops: InterviewOp[]) => {
    if (!state || busy) return;
    setBusy(true);
    const resposta = await InterviewService.turn(
      state.interview_id,
      mensagem,
      state.revision,
      undefined,
      ops
    );
    receber(resposta);
    setBusy(false);
    return resposta;
  };

  const alternarCamera2 = async (enabled: boolean) => {
    if (!state || state.status !== "aberta" || !state.scenario_reviewed || busy || aplicada || abasDoChecklistTrancadas) return;
    setBusy(true);
    try {
      receber(await InterviewService.setCamera2Enabled(
        state.interview_id, enabled, state.revision
      ));
    } finally {
      setBusy(false);
    }
  };

  /** Confirma a revisão humana e usa essa versão para gerar o Checklist. */
  const confirmarCenario = async () => {
    if (!state || busy || aplicada || importacaoAguardaLeitura(state) || camposVaziosDoCenario(state.draft) > 0)
      return;
    setBusy(true);
    try {
      const revisao = await InterviewService.reviewScenario(
        state.interview_id,
        state.revision
      );
      receber(revisao, () => abrirAba(1));

      if (revisao.kind === "ok" && !revisao.state.checklist_generated && !importacaoAguardaLeitura(revisao.state)) {
        setGerando(true);
        receber(
          await InterviewService.generate(
            revisao.state.interview_id,
            PEDIDO_CHECKLIST,
            revisao.state.revision,
            PEDIDO_CHECKLIST,
            "checklist"
          ),
          () => abrirAba(1),
          true
        );
      }
    } finally {
      setGerando(false);
      setBusy(false);
    }
  };

  /**
   * Retentativa explícita para uma sessão já revisada cuja geração falhou ou
   * que foi reaberta sem Checklist.
   */
  const gerarChecklist = async () => {
    if (
      !state ||
      busy ||
      gerando ||
      importacaoAguardaLeitura(state) ||
      !state.scenario_reviewed ||
      camposVaziosDoCenario(state.draft) > 0
    )
      return;
    setGerando(true);
    setBusy(true);
    try {
      receber(
        await InterviewService.generate(
          state.interview_id,
          PEDIDO_CHECKLIST,
          state.revision,
          PEDIDO_CHECKLIST,
          "checklist"
        ),
        () => abrirAba(1),
        true
      );
    } finally {
      setBusy(false);
      setGerando(false);
    }
  };

  const editarFaceta = (
    item: InterviewItem,
    campo: keyof InterviewDetection,
    texto: string
  ) =>
    editar(`Escrevi a regra **${campo}** de ${rotuloItem(item)} à mão.`, [
      { op: "set_detection", item_ref: item.ref, field: campo, text: texto },
    ]);

  const editarFacetaCamera2 = async (
    item: InterviewItem,
    campo: keyof InterviewCamera2Detection,
    texto: string
  ) => {
    const resposta = await editar(`Escrevi a regra **${campo}** da câmera focal de ${rotuloItem(item)} à mão.`, [
      { op: "set_detection_camera2", item_ref: item.ref, field: campo, text: texto },
    ]);
    if (!resposta) return;
    if (resposta.kind === "ok") {
      const salvo = resposta.state.draft.checklist.skills
        .flatMap((skill) => skill.items).find((atual) => atual.ref === item.ref);
      if ((salvo?.detection.camera2?.[campo] ?? "") !== texto.trim()) {
        const mensagem = t("interview_camera2_save_failed");
        setErro(mensagem);
        toast(mensagem, { type: "error" });
      }
    }
    setVersaoCamposCamera2((versao) => versao + 1);
  };

  const editarGuidanceCamera2 = async (texto: string) => {
    const resposta = await editar("Ajustei o enquadramento único da câmera focal para o checklist.", [
      { op: "set", path: "checklist.camera2_guidance", value: texto },
    ]);
    if (resposta?.kind === "ok" && (resposta.state.draft.checklist.camera2_guidance ?? "") !== texto.trim()) {
      const mensagem = t("interview_camera2_save_failed");
      setErro(mensagem);
      toast(mensagem, { type: "error" });
    }
    if (resposta) setVersaoCamposCamera2((versao) => versao + 1);
  };

  const editarPassosCamera2 = async (item: InterviewItem, passos: InterviewStep[]) => {
    const resposta = await editar(`Ajustei as etapas observáveis na câmera focal de ${rotuloItem(item)}.`, [
      { op: "set_steps_camera2", item_ref: item.ref, steps: passos },
    ]);
    if (resposta) setVersaoCamposCamera2((versao) => versao + 1);
  };

  const pedirEnquadramentoAoAssistente = () => {
    if (!state || aplicada || itens.length === 0) return;
    const emAndamento = enquadramentoAprofundar && (aprofundando || sincronizandoGpu);
    const propostaPronta = !aprofundando && !sincronizandoGpu && state.enrichment?.focus === FOCO_ENQUADRAMENTO;
    if (!emAndamento && !propostaPronta && (busy || aprofundando || sincronizandoGpu)) return;
    setLacunaFocada(null);
    setItemRefAprofundar(null);
    setItemNomeAprofundar(String(t("interview_camera2_guidance_title")));
    setCamera2Aprofundar(true);
    setEnquadramentoAprofundar(true);
    setPainelAprofundar(true);
    if (!emAndamento && !propostaPronta) void aprofundar(
      "Sugira ou revise uma única recomendação de posição, ângulo e enquadramento da câmera focal para todo o checklist. Determine o foco visual a partir do cenário, dos critérios e das regras existentes dos itens, respeitando o enquadramento já indicado quando pertinente. Não altere regras dos itens, pesos ou critérios.",
      FOCO_ENQUADRAMENTO, null, true, true
    );
  };

  /**
   * As Etapas obrigatórias do Item (`steps[]` da ADR-0003, que a tela chamava
   * de «critérios de aceite» — nome que o professor não reconhecia). A lista vai
   * INTEIRA, porque `set_steps` substitui: acrescentar, remover e reordenar são
   * a mesma op, e é isso que mantém o diff da Emenda legível para quem revisa.
   *
   * A frase relata a CONTAGEM em vez de listar as etapas: uma mensagem com doze
   * delas dentro empurraria a conversa toda para fora da tela.
   */
  const editarPassos = (item: InterviewItem, passos: InterviewStep[]) => {
    const ordenada = passos.some((p) => typeof p.order === "number");
    const relato =
      passos.length === 0
        ? `Removi as etapas obrigatórias de ${rotuloItem(item)}.`
        : `Escrevi ${passos.length} etapa(s) obrigatória(s) de ${rotuloItem(
            item
          )}${ordenada ? ", em ordem exigida" : ""}.`;

    return editar(relato, [
      { op: "set_steps", item_ref: item.ref, steps: passos },
    ]);
  };

  /**
   * Revisa as cinco facetas de qualquer Item como proposta seletiva.
   * Mesmo uma regra já preenchida pode ser melhorada; por isso esta ação não
   * escreve via turno e nunca some depois da primeira geração.
   */
  const pedirRegrasAoAssistente = async (item: InterviewItem, camera2 = false) => {
    if (!state) return;
    const emAndamento = itemRefAprofundar === item.ref && camera2Aprofundar === camera2 && (aprofundando || sincronizandoGpu);
    const propostaDoItem = !aprofundando && !sincronizandoGpu
      && state.enrichment?.focus === focoRegras(item.ref, camera2);
    if (emAndamento || propostaDoItem) {
      setEnquadramentoAprofundar(false);
      setItemRefAprofundar(item.ref);
      setItemNomeAprofundar(camera2 ? `${item.question} — Câmera focal` : item.question);
      setCamera2Aprofundar(camera2);
      setPainelAprofundar(true);
      return;
    }
    if (busy || aprofundando || sincronizandoGpu) return;
    setEnquadramentoAprofundar(false);
    setLacunaFocada(null);
    setItemRefAprofundar(item.ref);
    setItemNomeAprofundar(camera2 ? `${item.question} — Câmera focal` : item.question);
    setCamera2Aprofundar(camera2);
    setPainelAprofundar(true);
    void aprofundar(
      camera2 ? `Revise somente as regras visuais, aprovação, tolerâncias e etapas complementares da câmera focal do item **${item.question}**, determinando as evidências pertinentes a partir do cenário, do critério, das regras da câmera geral e do enquadramento disponível.` : `Revise somente as cinco regras de detecção do item **${item.question}**.`,
      null,
      item.ref,
      camera2,
      false
    );
  };

  // ---- os ganchos do painel de Lacunas -------------------------------
  //
  // O painel listava o que falta e não dava o que fazer com aquilo: era um
  // aviso, não uma ferramenta. Estes quatro verbos são o que uma Lacuna aceita,
  // e nenhum deles inventa caminho novo de escrita — todos passam pelas
  // operações que a tela já tinha.

  /**
   * Põe a Lacuna no pé da conversa. Não manda turno nenhum: só muda de quem é
   * a `forma` que a Resposta Guiada desenha.
   *
   * Por que isso basta: `RespostaGuiada` já sabe montar escolha, busca e a
   * cascata de criar Tema a partir de `gap.forma`. Ela só nunca tinha recebido
   * a forma de OUTRA Lacuna que não a corrente. O gancho que faltava era um
   * ponteiro, não uma tela.
   */
  const focarLacuna = (gap: InterviewGap) => {
    setLacunaFocada(gap);
    setMensagemAoFocar({ seq: state?.messages[state.messages.length - 1]?.seq ?? null });
    abrirAba(3);
  };

  /**
   * Leva ao campo que resolve a Lacuna. Uma Lacuna de `scenario.*` não se
   * responde conversando — se responde escrevendo no campo, e obrigar o
   * professor a descobrir em qual das quatro abas aquilo mora era transferir
   * a ele um mapeamento que o `caminho` já carrega.
   */
  const irParaCampo = (gap: InterviewGap) => {
    setLacunaFocada(gap);
    setMensagemAoFocar({ seq: state?.messages[state.messages.length - 1]?.seq ?? null });
    if (estreito) setCopilotoAberto(false);
    const destino = abaDaLacuna(gap);
    const checklistInteiro = destino === 1 && gap.caminho === "checklist.skills" && !gap.ref;
    const skills = state?.draft.checklist.skills || [];
    const indice = /skills\[(\d+)\]/.exec(gap.caminho);
    const skill = checklistInteiro ? undefined : (skills.find((s) => s.ref === gap.ref || s.items.some((i) => i.ref === gap.ref))
      || (indice ? skills[Number(indice[1])] : undefined)
      || (gap.codigo === "E08" ? skills.find((s) => s.points === null) : undefined)
      || skills[0]);
    const indiceItem = /items\[(\d+)\]/.exec(gap.caminho);
    const item = skill?.items.find((i) => i.ref === gap.ref)
      || (indiceItem ? skill?.items[Number(indiceItem[1])] : undefined);
    let alvo = `interview-field-${gap.caminho}`;
    if (destino === 1) {
      alvo = item ? `interview-checklist-item-${item.ref}`
        : skill ? `interview-checklist-section-${skill.ref}` : "interview-checklist";
      if (item && gap.caminho.includes("steps")) {
        setEtapasAbertas((atuais) => ({ ...atuais, [item.ref]: true }));
      }
    } else if (destino === 2 && skill) {
      setSecoesRegrasAbertas((atuais) => ({ ...atuais, [skill.ref]: true }));
      setItemRegraDestacado(null);
      alvo = item ? `interview-rule-item-${item.ref}` : `interview-rule-section-${skill.ref}`;
    } else if (destino === 4 && skill) {
      setSecoesRegrasCamera2Abertas((atuais) => ({ ...atuais, [skill.ref]: true }));
      alvo = item ? `camera2-rule-item-${item.ref}` : `camera2-section-${skill.ref}`;
    }
    setCampoDestino((atual) => ({
      id: alvo, caminho: gap.caminho, pedido: (atual?.pedido ?? 0) + 1,
      contornoCompleto: checklistInteiro || (!item && Boolean(skill) && [1, 2, 4].includes(destino)),
    }));
    abrirAba(destino);
  };

  useEffect(() => {
    if (!campoDestino) return;
    // Aguarda a abertura do Collapse para medir o item já na posição final.
    const timer = window.setTimeout(() => {
      const alvo = document.getElementById(campoDestino.id);
      setCampoDestino(null);
      if (!alvo) return;
      const reduzirMovimento = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      alvo.scrollIntoView?.({ behavior: reduzirMovimento ? "auto" : "smooth", block: "center" });
      const campo = campoDestino.id === "interview-checklist" || alvo.matches("input, textarea, button")
        ? alvo : alvo.querySelector<HTMLElement>("input:not(:disabled), textarea:not(:disabled), button:not(:disabled)");
      // O foco usa a própria borda de edição do campo, sem outra moldura no item.
      campo?.focus({ preventScroll: true });
      if (campoDestino.contornoCompleto) {
        alvo.animate?.([
          { boxShadow: `inset 0 0 0 3px ${palette.brand}`, backgroundColor: palette.brandSoft },
          { boxShadow: `inset 0 0 0 3px ${palette.brand}`, backgroundColor: palette.brandSoft, offset: 0.6 },
          { boxShadow: "inset 0 0 0 0 transparent", backgroundColor: "transparent" },
        ], { duration: reduzirMovimento ? 800 : 1600 });
      }
    }, 280);
    return () => window.clearTimeout(timer);
  }, [campoDestino]);

  /** Completa a pendência pelo mesmo turno do chat, com a pergunta selecionada. */
  const pedirAjuda = async (gap: InterviewGap) => {
    if (!state || busy || aplicada) return;
    setLacunaFocada(gap);
    setCopilotoAberto(true);
    setBusy(true);
    try {
      receber(await InterviewService.turn(
        state.interview_id,
        "Preencha esta pendência com a IA.",
        state.revision,
        undefined,
        undefined,
        { codigo: gap.codigo, ref: gap.ref, intent: "resolve" }
      ), (atualizado) => setLacunaFocada(atualizado.gaps.find(
        (atual) => atual.codigo === gap.codigo && atual.ref === gap.ref
      ) ?? null), true);
    } finally {
      setBusy(false);
    }
  };

  // ---- o Aprofundamento ----------------------------------------------

  /**
   * Abrir a "Revisão médica (MedGemma)" é o pedido QUANDO ainda não há nada:
   * dispara a revisão e mostra o painel em loading. Se já existe proposta ou
   * há uma varredura em voo, só abre - fechar e reabrir a gaveta não descarta
   * o que foi processado nem paga uma segunda rodada. Refinar é o follow-up
   * dentro do painel (ênfase + reprocessar).
   */
  const abrirAprofundar = () => {
    setLacunaFocada(null);
    setItemRefAprofundar(null);
    setCamera2Aprofundar(false);
    setEnquadramentoAprofundar(false);
    setItemNomeAprofundar(null);
    setPainelAprofundar(true);
    if (state?.enrichment?.amendments?.length) {
      setErro((atual) =>
        atual && (atual.includes("aprofundamento") || atual.includes("MedGemma"))
          ? null
          : atual
      );
    }
    if ((!state?.enrichment || ehFocoRegras(state.enrichment.focus) || state.enrichment.focus === FOCO_ENQUADRAMENTO) && !aprofundando && !sincronizandoGpu) {
      void aprofundar("", null, null, false, false);
    }
  };

  const aprofundar = async (
    steer?: string,
    gapWaiver?: string | null,
    itemRef?: string | null,
    camera2?: boolean,
    enquadramento?: boolean
  ) => {
    if (!state || aprofundando || sincronizandoGpu) return;
    const proposalIdAntes = state.enrichment?.proposal_id;
    setErro(null);
    setAprofundando(true);
    setBusy(true);

    const usaEnquadramento = enquadramento ?? enquadramentoAprofundar;
    const waiver = usaEnquadramento ? FOCO_ENQUADRAMENTO : gapWaiver !== undefined ? gapWaiver : lacunaFocada?.waiver;
    const ref = usaEnquadramento ? null : itemRef !== undefined ? itemRef : itemRefAprofundar;
    const usaCamera2 = camera2 !== undefined ? camera2 : camera2Aprofundar;

    const outcome = ref && usaCamera2 ? await InterviewService.enrich(
      state.interview_id, waiver ?? undefined, steer || undefined, ref, ["gemini"], "camera2"
    ) : await InterviewService.enrich(
      state.interview_id,
      waiver ?? undefined,
      steer || undefined,
      ref || undefined,
      ref || usaEnquadramento ? ["gemini"] : ["medgemma"]
    );

    if (outcome.kind === "ok") {
      receber(outcome);
      setBusy(false);
      setAprofundando(false);
      return;
    }

    // Uma falha de rede pode ocorrer depois de o servidor terminar a revisão.
    // Consulte o estado antes de apresentar erro ao professor.
    setAprofundando(false);
    setSincronizandoGpu(true);

    let recuperou = false;
    for (let i = 0; i < 15; i++) {
      type ResolversPolyfill = {
        withResolvers?: <T>() => { promise: Promise<T>; resolve: (value: T) => void };
      };
      const promiseConstructor = Promise as unknown as ResolversPolyfill;
      const resolver = promiseConstructor.withResolvers
        ? promiseConstructor.withResolvers<void>()
        : (() => {
            let res: () => void;
            const p = new Promise<void>((r) => {
              res = r;
            });
            return { promise: p, resolve: res! };
          })();
      setTimeout(resolver.resolve, 4000);
      await resolver.promise;
      try {
        const check = await InterviewService.show(state.interview_id);
        if (check.kind === "ok" && check.state.enrichment) {
          const propostaNova = check.state.enrichment;
          const ehNova =
            !proposalIdAntes || propostaNova.proposal_id !== proposalIdAntes;
          if (
            ehNova &&
            (usaEnquadramento ? propostaNova.focus === FOCO_ENQUADRAMENTO
              : !usaCamera2 || !ref || propostaNova.focus === focoRegras(ref, true)) &&
            (propostaNova.amendments?.length > 0 || propostaNova.summary)
          ) {
            receber(check);
            setErro(null);
            toast(usaEnquadramento ? "Recomendação de enquadramento recebida." : ref ? "Regras do item recebidas." : t("interview_enrich_syncing_success"), { type: "success" });
            recuperou = true;
            break;
          }
        }
      } catch {
        // ignora erro transitório durante polling
      }
    }

    setSincronizandoGpu(false);
    setBusy(false);

    if (!recuperou) {
      if (state.enrichment?.amendments?.length) {
        toast(
          "A nova chamada excedeu o tempo limite. Mantendo a proposta atual.",
          { type: "warning" }
        );
      } else {
        const msgAviso = ref || usaEnquadramento
          ? "Não foi possível receber as regras do Gemini. Tente novamente."
          : t("interview_enrich_cold_start_warning");
        setErro(msgAviso);
        toast(renderTextoComNegrito(msgAviso), { type: "warning" });
      }
    }
  };

  /**
   * Adota as Emendas marcadas. É turno: as `ops` delas entram pelo mesmo
   * produtor da edição manual nas abas, com `seen_revision` e lista branca.
   *
   * O painel só fecha quando a proposta acaba. Adotar cinco de vinte e ver a
   * gaveta fechar obrigaria a reabrir para continuar revisando as quinze.
   *
   * O toast diz EM QUAL ABA a mudança caiu. A maior parte do que o MedGemma
   * propõe é regra de detecção - aterrissa na aba 3, não no texto do
   * Checklist - e sem esse aviso o professor olha a aba 2 e conclui que
   * adotar não fez nada.
   */
  const adotarEmendas = async (ids: string[]) => {
    if (!state?.enrichment || adotando || ids.length === 0) return;
    const antes = state;
    setAdotando(true);
    setBusy(true);
    receber(
      await InterviewService.adoptEnrichment(
        state.interview_id,
        ids,
        state.revision,
        state.enrichment.proposal_id
      ),
      (s) => {
        if (s.revision > antes.revision) {
          const d = alteracoesDoRascunho(antes.draft, s.draft);
          const onde = [
            d.cenario > 0 && `${t("interview_tab_scenario")} (${d.cenario})`,
            d.checklist > 0 && `${t("interview_tab_correction")} (${d.checklist})`,
            d.regras > 0 && `${t("interview_tab_ai_rules")} (${d.regras})`,
            d.regrasCamera2 > 0 && `${t("interview_tab_ai_rules_camera2")} (${d.regrasCamera2})`,
          ].filter(Boolean);
          if (onde.length > 0) {
            toast(`${t("interview_enrich_applied_in")} ${onde.join(" · ")}`, {
              type: "success",
            });
          }
        }
        if (!s.enrichment || s.enrichment.amendments.length === 0) {
          setPainelAprofundar(false);
        }
      },
      true
    );
    setBusy(false);
    setAdotando(false);
  };

  const descartarAprofundamento = async () => {
    if (!state?.enrichment || adotando || aprofundando || sincronizandoGpu) return;
    receber(
      await InterviewService.discardEnrichment(
        state.interview_id,
        state.enrichment.proposal_id
      ),
      () => setPainelAprofundar(false)
    );
    setBusy(false);
  };

  // ---- aba Correção: seção e item ------------------------------------

  const editarSkill = (
    skill: InterviewSkill,
    campo: string,
    valor: string,
    rotuloCampo: string
  ) =>
    editar(`Ajustei ${rotuloCampo} da seção **${skill.name}** para **${valor}**.`, [
      { op: "set", path: `skill.${skill.ref}.${campo}`, value: campo !== "name" && valor.trim() === "" ? null : valor },
    ]);

  const editarItem = (
    item: InterviewItem,
    campo: string,
    valor: string,
    rotuloCampo: string
  ) =>
    editar(
      `Ajustei ${rotuloCampo} de ${rotuloItem(item)} para **${valor}**.`,
      [{ op: "set", path: `item.${item.ref}.${campo}`, value: campo === "value" && valor.trim() === "" ? null : valor }]
    );

  const alternarCritico = (item: InterviewItem) =>
    editar(
      item.critical
        ? `O ${rotuloItem(item)} deixou de ser eliminatório.`
        : `O ${rotuloItem(item)} passou a ser eliminatório.`,
      [{ op: "set_critical", item_ref: item.ref, value: !item.critical }]
    );

  const removerItem = (item: InterviewItem) =>
    editar(`Removi o ${rotuloItem(item)}.`, [
      { op: "remove_item", ref: item.ref },
    ]);

  const removerSkill = (skill: InterviewSkill) =>
    editar(`Removi a seção **${skill.name}**.`, [
      { op: "remove_skill", ref: skill.ref },
    ]);

  const adicionarItem = (skill: InterviewSkill) =>
    editar(`Criei um item na seção **${skill.name}**.`, [
      {
        op: "add_item",
        skill_ref: skill.ref,
        question: t("interview_new_item_question"),
        value: 0,
      },
    ]);

  const adicionarSkill = () =>
    editar(`Criei uma seção nova.`, [
      { op: "add_skill", name: t("interview_new_skill_name"), points: 0 },
    ]);

  // `waiver` é a string que veio no próprio gap. Montá-la aqui seria supor a
  // regra de composição do servidor (`codigo` ou `codigo:ref`).
  const dispensar = async (gap: InterviewGap) => {
    if (!state || busy) return;
    setBusy(true);
    setCopilotoAberto(true);
    try {
      receber(await InterviewService.waive(state.interview_id, gap.waiver, true), () => setLacunaFocada(null));
    } finally {
      setBusy(false);
    }
  };

  /** Reabrir edita no mesmo lugar; o 409 do servidor já sugere duplicar. */
  const reabrir = async () => {
    if (!state || busy) return;
    setBusy(true);
    receber(await InterviewService.reopen(state.interview_id));
    setBusy(false);
  };

  /** Duplicar leva para a cópia: é nela que ele vai trabalhar agora. */
  const duplicarEntrevista = async () => {
    if (!state || busy) return;
    setBusy(true);
    const r = await InterviewService.duplicate(state.interview_id);
    setBusy(false);

    if (r.kind !== "ok") {
      receber(r);
      return;
    }

    toast(t("interview_duplicated"), { type: "success" });
    navigate(`/interview/detail/${r.state.interview_id}`);
  };

  const revogar = async (waiver: string) => {
    if (!state || busy) return;
    setBusy(true);
    receber(await InterviewService.waive(state.interview_id, waiver, false));
    setBusy(false);
  };

  const aplicar = async () => {
    if (!state || applying || busy || importacaoPendente(state)) return;
    setApplying(true);
    // Só aqui pode haver toast verde, e só depois de o servidor devolver os
    // ids. Um 409 cai em `conflict` e um 5xx em `failed` — nenhum dos dois
    // passa por este ramo. Era exatamente esse o defeito D-1 do painel antigo.
    receber(await InterviewService.apply(state.interview_id), (s) => {
      if (!s.applied) return;
      toast(t("interview_applied_toast"), { type: "success" });
      abrirAba(1);
    });
    setApplying(false);
  };

  if (carregando) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!state) {
    return (
      <Box sx={{ p: 4, maxWidth: 700, mx: "auto" }}>
        <Alert severity="error">{erro || t("interview_not_found")}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/interview/list")}>
          {t("interview_back_to_list")}
        </Button>
      </Box>
    );
  }

  const refPropostaRegras = ehFocoRegras(state.enrichment?.focus)
    ? state.enrichment?.focus?.split(":")[1] ?? null
    : null;
  const refRegrasParaReabrir = aprofundando || sincronizandoGpu
    ? itemRefAprofundar
    : refPropostaRegras;

  // Antes da confirmação, uma Lacuna do Checklist ainda não é a pergunta
  // corrente da interface: a ação válida é revisar e confirmar o Cenário.
  const lacuna =
    state.next_question &&
    gapsDaFase.some(
      (gap) =>
        gap.codigo === state.next_question?.codigo &&
        (gap.ref || "") === (state.next_question?.ref || "")
    )
      ? state.next_question
      : null;

  // A Lacuna EM FOCO — a que a Resposta Guiada desenha e o painel destaca.
  //
  // É a escolhida pelo professor, mas RECONFERIDA contra a lista que o servidor
  // acabou de mandar: se o turno anterior fechou justamente aquela Lacuna, ela
  // sumiu de `gaps` e insistir nela deixaria a tela oferecendo resposta para
  // uma pergunta que não existe mais. Sumiu, volta para a corrente. É a mesma
  // política de self-healing do servidor, do lado de cá.
  const emFoco =
    (lacunaFocada &&
      gapsDaFase.find(
        (g) =>
          g.codigo === lacunaFocada.codigo &&
          (g.ref || "") === (lacunaFocada.ref || "")
      )) ||
    lacuna;

  // Vão reservado à direita dos rodapés de ação para o dock do Copiloto. Numa
  // tela estreita com o Copiloto ABERTO ele cobre a largura toda: não existe
  // vão que salve o rodapé e reservar a linha inteira só quebraria o layout —
  // ali o caminho é minimizar o Copiloto, e nesse estado o vão volta.
  const vaoCopiloto =
    larguraDock > 0 && !(estreito && copilotoAberto)
      ? `${larguraDock + 16}px`
      : undefined;

  // As mensagens do chat enriquecidas com ações da Lacuna associada.
  const mensagensAtuais = [...(state?.messages || [])];
  const ultima = mensagensAtuais[mensagensAtuais.length - 1];
  // Selecionar uma lacuna não cria turno. Só nesse caso mostramos a pergunta
  // escolhida: uma clarificação ou aviso real do assistente deve continuar
  // como a última fala, sem receber outra pergunta canônica por cima.
  if (lacunaFocada && emFoco && emFoco.codigo === lacunaFocada.codigo && emFoco.ref === lacunaFocada.ref
      && mensagemAoFocar && (ultima?.seq ?? null) === mensagemAoFocar.seq
      && !busy && (ultima?.role !== "assistente" || !ultima.text.includes(emFoco.pergunta))) {
    mensagensAtuais.push({ seq: -1, role: "assistente", text: emFoco.pergunta, gap_code: emFoco.codigo, degraded: false, rejected: false, at: "" });
  }
  const mensagens: ChatMessage[] = mensagensAtuais.map((m, index, arr) => {
    const avisos: string[] = [];
    if (m.degraded) avisos.push(t("interview_msg_degraded"));
    if (m.rejected) avisos.push(t("interview_msg_rejected"));

    if (m.role === "professor") {
      return {
        key: m.seq,
        mine: true,
        text: m.text,
        content: mensagemArquivos(m.text) ?? <Typography className="interview-chat-message" variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{renderTextoComNegrito(m.text)}</Typography>,
        caption: avisos.length > 0 ? avisos.join(" · ") : undefined,
      };
    }

    const gapDaMensagem = index === arr.length - 1 && emFoco && m.text.includes(emFoco.pergunta) ? emFoco : null;

    const botoesAcao = gapDaMensagem && !aplicada ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
          mt: 1,
          pt: 0.75,
          borderTop: `1px solid ${palette.border}`,
        }}
      >
        {abaDaLacuna(gapDaMensagem) !== 3 && (
          <Button
            size="small"
            disabled={busy}
            onClick={() => irParaCampo(gapDaMensagem)}
            sx={{
              minWidth: 0,
              px: 0.75,
              py: 0,
              fontSize: 11,
              lineHeight: 1.7,
              fontWeight: 600,
              textTransform: "none",
              color: palette.brand,
              "&:hover": { background: palette.brandSoft },
            }}
          >
            {t("interview_gap_open_field")}
          </Button>
        )}
        {gapDaMensagem.forma.tipo === "texto" && (
          <Button
            size="small"
            disabled={busy}
            onClick={() => pedirAjuda(gapDaMensagem)}
            sx={{
              minWidth: 0,
              px: 0.75,
              py: 0,
              fontSize: 11,
              lineHeight: 1.7,
              fontWeight: 600,
              textTransform: "none",
              color: palette.brand,
              "&:hover": { background: palette.brandSoft },
            }}
          >
            {t("interview_gap_ask_ai")}
          </Button>
        )}
        {gapDaMensagem.dispensavel && (
          <Button
            size="small"
            disabled={busy}
            onClick={() => dispensar(gapDaMensagem)}
            sx={{
              minWidth: 0,
              px: 0.75,
              py: 0,
              fontSize: 11,
              lineHeight: 1.7,
              fontWeight: 500,
              textTransform: "none",
              color: palette.textMuted,
              "&:hover": { color: palette.brand, background: palette.brandSoft },
            }}
          >
            {t("interview_waive")}
          </Button>
        )}
      </Box>
    ) : null;

    return {
      key: m.seq,
      mine: false,
      text: m.text,
      content: (
        <Box sx={{ whiteSpace: "normal" }}>
          <Typography className="interview-chat-message" variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {renderTextoComNegrito(m.text)}
          </Typography>
          {botoesAcao}
        </Box>
      ),
      caption: avisos.length > 0 ? avisos.join(" · ") : undefined,
    };
  });

  // ── as duas esperas, e por que a tela as trata diferente ────────────────
  //
  // `escrevendo` é a espera LONGA: o modelo está montando Cenário/Checklist e
  // vai REESCREVER os campos que estão na tela. Aqui a trava tem de ser
  // visível e total — quem digitasse por baixo perderia o texto quando o
  // Rascunho novo chegasse, que é o defeito relatado («o loading só existe
  // dentro do chatbot»).
  //
  // `salvando` é a espera CURTA de um turno comum (uma edição de campo, uma
  // resposta guiada). Cobrir a aba a cada tecla comitada seria pior que não
  // avisar nada: a página piscaria a cada campo. Essa espera ganha só o fio de
  // progresso no topo do quadro e o selo no cabeçalho.
  const escrevendo = gerando || adotando;
  const salvando = (busy || applying) && !escrevendo;
  const ocupado = escrevendo || salvando;
  // Duas frases para a mesma espera, porque os dois lugares têm larguras
  // diferentes: no selo do cabeçalho cabe um rótulo de chip; na camada, que
  // ocupa a aba inteira, cabe a frase que explica a trava.
  const seloEspera = t(
    escrevendo
      ? "interview_ai_writing_badge"
      : applying
      ? "interview_applying"
      : "interview_saving"
  );
  const tituloEspera = t(
    adotando
      ? "interview_enrich_adopting_title"
      : "interview_fields_locked_title"
  );
  const detalheEspera = t(
    adotando
      ? "interview_enrich_adopting_fields"
      : state.scenario_reviewed
      ? "interview_generate_checklist_waiting"
      : "interview_generate_waiting"
  );

  return (
    // A página é uma coluna flex que ocupa EXATAMENTE o que o template lhe deu
    // (`contentContainer` é `flex: 1` dentro de uma coluna de 100vh). Nada de
    // `pb: 8` reservando vão no fim: aquele respiro existia para compensar o
    // rodapé que invadia, e o rodapé não invade mais.
    <Box
      sx={{
        p: 2.5,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        gap: 1.5,
        overflowY: "auto",
      }}
    >
      {/* Cabeçalho enxuto: nome, situação e contexto numa faixa só. Era um
          `Paper` de 2.5 de padding com os chips numa segunda linha, e gastava
          uns 100px de altura para dizer o que cabe em 40 — altura que faz
          falta embaixo, na conversa. */}
      <Paper sx={{ px: 2, py: 1.25, flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <AutoAwesomeOutlinedIcon
            fontSize="small"
            sx={{ color: palette.brand }}
          />
          <Typography
            variant="h6"
            sx={{ color: palette.brandDark, flexGrow: 1 }}
          >
            {state.draft.name || t("interview_unnamed")}
          </Typography>
          <Chip
            size="small"
            color={
              aplicada
                ? "success"
                : bloqueantes.length === 0
                ? "primary"
                : "default"
            }
            variant={aplicada ? "filled" : "outlined"}
            label={t(`interview_status_${state.status}`)}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`${t("interview_revision")} ${state.revision}`}
          />
          {/* Selo de espera no cabeçalho: é a única parte da página que não
              sai de cena em nenhuma aba, então é aqui que a espera aparece
              mesmo com o Copiloto minimizado. */}
          {ocupado && (
            <Chip
              size="small"
              data-testid="interview-selo-espera"
              icon={
                <CircularProgress
                  size={12}
                  thickness={6}
                  sx={{ color: `${palette.brand} !important`, ml: 0.75 }}
                />
              }
              label={seloEspera}
              sx={{
                bgcolor: palette.brandSoft,
                color: palette.brandDark,
                fontWeight: 600,
                border: `1px solid ${palette.brand}33`,
              }}
            />
          )}
        </Box>
        {/* Contexto e Objetivo ficam numa linha com reticências no desktop. No
            celular, os chips e o objetivo quebram: cortar o último dado de
            contexto para economizar uma linha escondia justamente a duração. */}
        {(contexto.length > 0 || state.draft.scenario.goal_scene) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.75,
              minWidth: 0,
              flexWrap: "wrap",
            }}
          >
            {contexto.map((c) => (
              <Chip
                key={c}
                size="small"
                label={c}
                sx={{ height: 20, fontSize: 11 }}
              />
            ))}
            {state.draft.scenario.goal_scene && (
              <Typography
                variant="caption"
                title={state.draft.scenario.goal_scene}
                sx={{
                  color: palette.textMuted,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                  flexGrow: 1,
                  flexBasis: { xs: "100%", sm: "auto" },
                }}
              >
                {state.draft.scenario.goal_scene}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {state.degraded && (
        <Alert severity="warning" sx={{ flexShrink: 0 }}>
          {t("interview_degraded")}
        </Alert>
      )}

      {erro && (
        <Alert
          severity="error"
          sx={{ flexShrink: 0 }}
          onClose={() => setErro(null)}
        >
          {renderTextoComNegrito(erro)}
        </Alert>
      )}

      {aplicada && state.applied && (
        <Alert
          severity="success"
          sx={{
            flexShrink: 0,
            flexWrap: { xs: "wrap", sm: "nowrap" },
            "& .MuiAlert-action": {
              flexBasis: { xs: "100%", sm: "auto" },
              ml: { xs: 0, sm: "auto" },
              pt: { xs: 1, sm: 0 },
            },
          }}
          action={
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                flexWrap: "wrap",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              {/* Primeiro botão de propósito: é no Cenário que os dois PDFs de
                  instrução (Ator e Avaliado) aparecem depois da Aplicação. Um
                  Checklist legado adotado pode ainda não ter Cenário. */}
              {state.applied.scenario_id !== null && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() =>
                    navigate(`/scenarios/form/${state.applied?.scenario_id}`)
                  }
                >
                  {t("interview_open_scenario_legacy")}
                </Button>
              )}
              {state.applied.checklist_id !== null && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() =>
                    navigate(`/checklist/form/${state.applied?.checklist_id}`)
                  }
                >
                  {t("interview_open_checklist_legacy")}
                </Button>
              )}
              {/* Reabrir edita NO MESMO LUGAR e o servidor recusa com 409 se o
                  Checklist já estiver em uso — não escondo o botão porque só o
                  servidor sabe a resposta, e a mensagem dele aponta duplicar. */}
              <Button
                color="inherit"
                size="small"
                disabled={busy}
                onClick={reabrir}
              >
                {t("interview_reopen")}
              </Button>
              <Button
                color="inherit"
                size="small"
                disabled={busy}
                onClick={duplicarEntrevista}
              >
                {t("interview_duplicate")}
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={busy || publicando || publicado}
                onClick={publicar}
                sx={{
                  color: palette.surface,
                  bgcolor: palette.emeraldDeep,
                  border: `1px solid ${palette.emeraldDeep}`,
                  fontWeight: 700,
                  "&:hover": { bgcolor: palette.emeraldDeep },
                }}
              >
                {publicando
                  ? t("interview_publishing")
                  : publicado
                  ? t("interview_published_label")
                  : t("interview_publish")}
              </Button>
            </Box>
          }
        >
          {t("interview_applied_banner")}
          {state.applied.scenario_id !== null &&
            ` — ${t("interview_scenario")} ${state.applied.scenario_id} ·`}{" "}
          {t("interview_checklist")} {state.applied.checklist_id}
        </Alert>
      )}

      {/* Era `height: "68vh"` — número chutado que ignorava o espaço real: numa
          tela de 1000px sobravam ~340px para a conversa enquanto 150px morriam
          embaixo. Agora o quadro ocupa o que sobrou da coluna e nada mais, e
          `minHeight: 0` é o que permite que os filhos rolem em vez de empurrar
          a página. O piso de 380px existe para telas muito baixas: abaixo
          disso a conversa deixa de ser usável e é melhor a página rolar. */}
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 480,
          position: "relative",
        }}
      >
        <Tabs
          value={aba}
          onChange={(_, v) => abrirAba(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 44, bgcolor: palette.surface }}
        >
          <Tab
            value={0}
            sx={{ minHeight: 44, fontSize: 13, fontWeight: 600 }}
            label={
              <Badge
                color="secondary"
                badgeContent={
                  novasAlteracoesCenario > 0
                    ? `+${novasAlteracoesCenario}`
                    : undefined
                }
                sx={{
                  "& .MuiBadge-badge": {
                    right: -14,
                    top: 2,
                    bgcolor: palette.brand,
                  },
                }}
              >
                {t("interview_tab_scenario")}
              </Badge>
            }
          />
          <Tab
            value={1}
            disabled={abasDoChecklistTrancadas}
            sx={{ minHeight: 44, fontSize: 13, fontWeight: 600 }}
            label={
              <Badge
                color="secondary"
                badgeContent={
                  novasAlteracoesChecklist > 0
                    ? `+${novasAlteracoesChecklist}`
                    : undefined
                }
                sx={{
                  "& .MuiBadge-badge": {
                    right: -14,
                    top: 2,
                    bgcolor: palette.brand,
                  },
                }}
              >
                {t("interview_tab_correction")}
              </Badge>
            }
          />
          <Tab
            value={2}
            disabled={abasDoChecklistTrancadas || itens.length === 0}
            sx={{ minHeight: 44, fontSize: 13, fontWeight: 600 }}
            label={
              <Badge
                color={novasAlteracoesRegras > 0 ? "secondary" : "warning"}
                badgeContent={
                  novasAlteracoesRegras > 0
                    ? `+${novasAlteracoesRegras}`
                    : nRevisar > 0
                    ? nRevisar
                    : undefined
                }
                sx={{ "& .MuiBadge-badge": { right: -14, top: 2 } }}
              >
                {t("interview_tab_ai_rules")}
              </Badge>
            }
          />
          <Tab
            value={4}
            disabled={abasDoChecklistTrancadas || itens.length === 0}
            sx={{ minHeight: 44, fontSize: 13, fontWeight: 600 }}
            label={
              <Badge color="secondary" badgeContent={novasAlteracoesRegrasCamera2 > 0 ? `+${novasAlteracoesRegrasCamera2}` : undefined}
                sx={{ "& .MuiBadge-badge": { right: -14, top: 2 } }}>
                {t("interview_tab_ai_rules_camera2")}
              </Badge>
            }
          />
          <Tab
            value={3}
            sx={{ minHeight: 44, fontSize: 13, fontWeight: 600 }}
            label={
              <Badge
                color="error"
                badgeContent={
                  bloqueantes.length > 0 && itens.length > 0
                    ? bloqueantes.length
                    : undefined
                }
                sx={{ "& .MuiBadge-badge": { right: -14, top: 2 } }}
              >
                {t("interview_tab_conversation")}
              </Badge>
            }
          />
        </Tabs>
        <Divider />

        {/* ── ÁREA EDITÁVEL: o que a espera longa cobre ─────────────────────
            Uma casca só para as quatro abas, para a sobreposição cobrir o
            conteúdo e NÃO as abas: navegar entre elas continua valendo
            enquanto a IA escreve (o professor quer ver o que ela está
            mexendo), editar não. O `position: relative` é o que ancora a
            camada; sem ele ela subiria até o `Paper` e comeria as abas. */}
        <Box
          sx={{
            position: "relative",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {/* Fio de progresso rente à borda das abas — vale para as DUAS
              esperas, inclusive a curta, e é absoluto de propósito: entrar no
              fluxo empurraria o quadro 4px a cada turno salvo. */}
          {ocupado && (
            <LinearProgress
              data-testid="interview-fio-progresso"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 3,
                height: 3,
                bgcolor: palette.brandSoft,
                "& .MuiLinearProgress-bar": { bgcolor: palette.brand },
              }}
            />
          )}

          {/* O conteúdo em si. Durante a escrita ele apaga, desfoca e pulsa:
              é a «animação nos campos» que faltava. `pointerEvents: none`
              trava a área inteira — inclusive campo novo que alguém acrescente
              aqui amanhã e esqueça de ligar no `disabled`. `inert` não dá:
              não existe no React 18 nem em Safari, e sem ele o `tabIndex`
              seguiria alcançável, por isso `aria-hidden` fica de fora e a
              sobreposição é quem fala com o leitor de tela. */}
          <Box
            data-testid="interview-area-editavel"
            aria-busy={escrevendo}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              transition: "opacity .2s ease, filter .2s ease",
              ...(escrevendo && {
                pointerEvents: "none",
                userSelect: "none",
                filter: "blur(1.5px) saturate(0.85)",
                "@keyframes respirar": {
                  "0%, 100%": { opacity: 0.42 },
                  "50%": { opacity: 0.62 },
                },
                animation: "respirar 1.8s ease-in-out infinite",
              }),
            }}
          >
            {/* ── aba 0 · cenário (editável) ───────────────────────────────── */}
            {aba === 0 && (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <AbaCenario
                  campoEmFoco={campoDestino?.caminho}
                  pedidoCampo={campoDestino?.pedido}
                  draft={state.draft}
                  busy={busy || aplicada}
                  onEditar={editar}
                />
                <Box
                  sx={{
                    p: 2,
                    pr: vaoCopiloto,
                    display: "flex",
                    justifyContent: "flex-end",
                    borderTop: `1px solid ${palette.border}`,
                  }}
                >
                  <Button
                    variant="contained"
                    disabled={
                      busy ||
                      aplicada ||
                      importacaoAguardaLeitura(state) ||
                      (!state.scenario_reviewed && vaziosCenario > 0)
                    }
                    startIcon={
                      !state.scenario_reviewed ? (
                        <CheckCircleOutlineIcon fontSize="small" />
                      ) : undefined
                    }
                    endIcon={<ArrowForwardIcon />}
                    onClick={confirmarCenario}
                  >
                    {!state.scenario_reviewed
                      ? t("interview_confirm_scenario")
                      : t("interview_advance_to_checklist")}
                  </Button>
                </Box>
              </Box>
            )}

            {/* ── aba 1 · checklist / correção (editável) ──────────────────── */}
            {aba === 1 && !abasDoChecklistTrancadas && (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <Box
                  id="interview-checklist"
                  tabIndex={-1}
                  sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 1.5, outline: "none" }}
                >
                  {importacaoPendente(state) && (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      Há uma importação pendente. Acompanhe o processamento e as decisões no Assistente IA antes de gerar outro Checklist.
                    </Alert>
                  )}
                  {itens.length === 0 &&
                    state.draft.checklist.skills.length === 0 && (
                      <Box sx={{ p: 3 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: palette.textMuted, mb: 1.5 }}
                        >
                          {t("interview_empty_correction")}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={
                            busy ||
                            aplicada ||
                            importacaoAguardaLeitura(state) ||
                            !state.scenario_reviewed ||
                            vaziosCenario > 0
                          }
                          startIcon={
                            <AutoAwesomeOutlinedIcon fontSize="small" />
                          }
                          onClick={gerarChecklist}
                        >
                          {t("interview_generate_checklist")}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={
                            busy || aplicada || !state.scenario_reviewed
                          }
                          onClick={adicionarSkill}
                          sx={{ ml: 1 }}
                        >
                          {t("interview_add_skill")}
                        </Button>
                      </Box>
                    )}

                  {state.draft.checklist.skills.length > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        color: palette.textMuted,
                        display: "block",
                        mb: 1,
                      }}
                    >
                      {itens.length} {t("interview_items")}
                      {nCriticos > 0 &&
                        ` · ${nCriticos} ${t("interview_criticals")}`}
                    </Typography>
                  )}

                  {state.draft.checklist.skills.map((skill) => (
                    <Box
                      key={skill.ref}
                      id={`interview-checklist-section-${skill.ref}`}
                      sx={{
                        mb: 2.5,
                        overflow: "hidden",
                        border: `1px solid ${palette.border}`,
                        borderRadius: 2,
                        bgcolor: "#fff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr)",
                          alignItems: "stretch",
                          gap: 1,
                          p: 1.5,
                          bgcolor: palette.brandSoft + "20",
                          borderBottom: `2px solid ${palette.brandSoft}`,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <CampoEditavel
                            valor={skill.name}
                            label={t("interview_section_label") ?? undefined}
                            fullWidth
                            multiline
                            minRows={1}
                            maxRows={3}
                            confirmarComEnter
                            disabled={busy || aplicada}
                            onComitar={(v) =>
                              editarSkill(
                                skill,
                                "name",
                                v,
                                t("interview_field_name")
                              )
                            }
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "flex-start",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <CampoEditavel
                            valor={skill.points === null ? "" : String(skill.points)}
                            label={t("interview_field_points") ?? undefined}
                            numero
                            largura={104}
                            disabled={busy || aplicada}
                            onComitar={(v) =>
                              editarSkill(
                                skill,
                                "points",
                                v,
                                t("interview_field_points")
                              )
                            }
                          />
                          <CampoEditavel
                            valor={skill.passing_score === null ? "" : String(skill.passing_score)}
                            label={t("interview_field_cut") ?? undefined}
                            numero
                            largura={124}
                            disabled={busy || aplicada}
                            onComitar={(v) =>
                              editarSkill(
                                skill,
                                "passing_score",
                                v,
                                t("interview_field_cut")
                              )
                            }
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busy || aplicada}
                            onClick={() => adicionarItem(skill)}
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {t("interview_add_item")}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={busy || aplicada}
                            onClick={() => removerSkill(skill)}
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {t("interview_remove")}
                          </Button>
                        </Box>
                      </Box>

                      {skill.items.map((item) => {
                        const det = avaliarDeteccao(item);
                        // As Etapas do Item (ADR-0003) aparecem AQUI, na aba em
                        // que o professor monta o Checklist, e não só na aba das
                        // regras da IA: sem sinal nenhum nesta lista, quem
                        // escreve o Item não descobre que pode decompô-lo — foi
                        // exatamente o relato do usuário.
                        const etapas = item.detection?.steps ?? [];
                        const etapasVisiveis = etapasAbertas[item.ref] ?? false;
                        return (
                          <Box
                            key={item.ref}
                            id={`interview-checklist-item-${item.ref}`}
                            sx={{
                              borderBottom: `1px solid ${palette.border}`,
                              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderWidth: 1,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.5,
                                py: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Tooltip
                                title={`${ROTULOS[det.nivel]} — ${det.motivo}`}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 24,
                                    borderRadius: 3,
                                    flexShrink: 0,
                                    backgroundColor: COR_NIVEL[det.nivel],
                                  }}
                                />
                              </Tooltip>
                              <Box sx={{ flex: "1 1 520px", minWidth: 0 }}>
                                <CampoEditavel
                                  valor={item.question}
                                  label={numeroPorItemRef[item.ref] ? `Item ${numeroPorItemRef[item.ref]}` : undefined}
                                  fullWidth
                                  multiline
                                  disabled={busy || aplicada}
                                  onComitar={(v) =>
                                    editarItem(
                                      item,
                                      "question",
                                      v,
                                      t("interview_field_question")
                                    )
                                  }
                                />
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  gap: 1,
                                  ml: "auto",
                                  flexWrap: "wrap",
                                }}
                              >
                                {/* O cue das Etapas. Fica ANTES de «crítico»
                                  porque são os dois controles de invalidação
                                  do Item e o professor lê os dois juntos:
                                  crítico invalida o desempenho, etapa faltante
                                  derruba este Item. */}
                                <Tooltip
                                  title={renderTextoComNegrito(t(
                                    etapas.length > 0
                                      ? "interview_item_steps_hint"
                                      : "interview_item_steps_hint_empty"
                                  ))}
                                >
                                  <Chip
                                    size="small"
                                    icon={
                                      <ChecklistRtlIcon sx={{ fontSize: 16 }} />
                                    }
                                    color={
                                      etapas.length > 0 ? "primary" : "default"
                                    }
                                    variant={
                                      etapas.length > 0 ? "filled" : "outlined"
                                    }
                                    aria-expanded={etapasVisiveis}
                                    aria-label={`${t(
                                      etapas.length > 0
                                        ? "interview_item_steps"
                                        : "interview_item_steps_define"
                                    )} ${item.question}`}
                                    label={
                                      etapas.length > 0
                                        ? `${etapas.length} ${t(
                                            "interview_item_steps"
                                          )}`
                                        : t("interview_item_steps_define")
                                    }
                                    onClick={() =>
                                      setEtapasAbertas((atuais) => ({
                                        ...atuais,
                                        [item.ref]: !etapasVisiveis,
                                      }))
                                    }
                                    sx={{ cursor: "pointer" }}
                                  />
                                </Tooltip>
                                <Tooltip title={t("interview_toggle_critical")}>
                                  <Chip
                                    size="small"
                                    color={item.critical ? "error" : "default"}
                                    variant={
                                      item.critical ? "filled" : "outlined"
                                    }
                                    label={t("interview_critical")}
                                    disabled={busy || aplicada}
                                    onClick={() => alternarCritico(item)}
                                    sx={{
                                      cursor:
                                        busy || aplicada
                                          ? "default"
                                          : "pointer",
                                    }}
                                  />
                                </Tooltip>
                                <CampoEditavel
                                  valor={item.value === null ? "" : String(item.value)}
                                  label={
                                    t("interview_item_weight") ?? undefined
                                  }
                                  numero
                                  largura={88}
                                  disabled={busy || aplicada}
                                  onComitar={(v) =>
                                    editarItem(
                                      item,
                                      "value",
                                      v,
                                      t("interview_field_value")
                                    )
                                  }
                                />
                                <Button
                                  size="small"
                                  color="error"
                                  disabled={busy || aplicada}
                                  aria-label={`${t("interview_remove")} ${
                                    item.question
                                  }`}
                                  onClick={() => removerItem(item)}
                                  sx={{
                                    minWidth: 0,
                                    px: 0.5,
                                    fontSize: 10,
                                    textTransform: "none",
                                  }}
                                >
                                  {t("interview_remove")}
                                </Button>
                              </Box>
                            </Box>

                            {/* O MESMO editor da aba de regras, com o MESMO
                                `set_steps`: duas portas para uma escrita só.
                                As abas nunca coexistem no DOM, então não há
                                dois estados locais do mesmo Item no ar. */}
                            <Collapse
                              in={etapasVisiveis}
                              timeout={200}
                              unmountOnExit
                            >
                              <Box
                                sx={{
                                  px: 2.5,
                                  pb: 1.5,
                                  bgcolor: palette.background,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: palette.textMuted,
                                    display: "block",
                                  }}
                                >
                                  {renderTextoComNegrito(t("interview_item_steps_hint"))}
                                </Typography>
                                <PassosDoItem
                                  passos={etapas}
                                  busy={busy || aplicada}
                                  onComitar={(passos) =>
                                    editarPassos(item, passos)
                                  }
                                />
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Box>
                  ))}

                  {state.draft.checklist.skills.length > 0 && !aplicada && (
                    <Button
                      size="small"
                      disabled={busy}
                      onClick={adicionarSkill}
                    >
                      {t("interview_add_skill")}
                    </Button>
                  )}
                </Box>
                <Box
                  sx={{
                    p: 2,
                    pr: vaoCopiloto,
                    display: "flex",
                    justifyContent: "space-between",
                    borderTop: `1px solid ${palette.border}`,
                  }}
                >
                  <Button onClick={() => abrirAba(0)}>
                    {t("interview_wizard_back")}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={busy || itens.length === 0}
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => abrirAba(2)}
                  >
                    {t("interview_advance_to_rules")}
                  </Button>
                </Box>
              </Box>
            )}

            {/* ── aba 2 · estrutura e regras da IA (editável) ─────────────── */}
            {aba === 2 && !abasDoChecklistTrancadas && (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                    bgcolor: palette.surface,
                    borderBottom: `1px solid ${palette.border}`,
                  }}
                >
                  <Box sx={{ flex: "1 1 320px" }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, color: palette.text }}
                    >
                      {t("interview_rules_title")}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: palette.textMuted }}
                    >
                      {t("interview_rules_help")}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {!painelAprofundar && refRegrasParaReabrir && (
                        <Button
                          size="small"
                          onClick={() => {
                            const item = state.draft.checklist.skills
                              .flatMap((skill) => skill.items)
                              .find((candidate) => candidate.ref === refRegrasParaReabrir);
                            if (item) pedirRegrasAoAssistente(item, aprofundando || sincronizandoGpu
                              ? camera2Aprofundar : Boolean(state.enrichment?.focus?.startsWith("ITEM_RULES_CAMERA2:")));
                          }}
                          startIcon={<AutoAwesomeOutlinedIcon fontSize="small" />}
                        >
                          {aprofundando || sincronizandoGpu ? "ANÁLISE EM ANDAMENTO" : "Ver regras sugeridas"}
                        </Button>
                      )}
                    <Chip
                      size="small"
                      label={`${state.draft.checklist.skills.length} ${t(
                        "interview_col_sections"
                      )}`}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      icon={
                        resumoDeteccoesAberto === "forte" ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )
                      }
                      label={rotuloContagemDeteccao(
                        itensPorGrupo.forte.length,
                        "forte"
                      )}
                      color="success"
                      variant="outlined"
                      clickable={itensPorGrupo.forte.length > 0}
                      disabled={itensPorGrupo.forte.length === 0}
                      aria-expanded={resumoDeteccoesAberto === "forte"}
                      aria-controls="interview-detection-summary"
                      onClick={() =>
                        setResumoDeteccoesAberto((atual) =>
                          atual === "forte" ? null : "forte"
                        )
                      }
                      sx={{ fontWeight: 600 }}
                    />
                    {nRevisar > 0 && (
                      <Chip
                        size="small"
                        icon={
                          resumoDeteccoesAberto === "revisar" ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )
                        }
                        label={rotuloContagemDeteccao(nRevisar, "revisar")}
                        color="warning"
                        variant="outlined"
                        clickable
                        aria-expanded={resumoDeteccoesAberto === "revisar"}
                        aria-controls="interview-detection-summary"
                        onClick={() =>
                          setResumoDeteccoesAberto((atual) =>
                            atual === "revisar" ? null : "revisar"
                          )
                        }
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                    {!aplicada && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busy}
                        onClick={adicionarSkill}
                      >
                        {t("interview_add_skill")}
                      </Button>
                    )}
                  </Box>
                </Box>
                {resumoDeteccoesAberto && (
                  <Paper
                    id="interview-detection-summary"
                    role="region"
                    aria-label={t("interview_detection_summary_title")}
                    elevation={0}
                    sx={{
                      mx: 1.5,
                      mt: 1.5,
                      overflow: "hidden",
                      border: `1px solid ${palette.border}`,
                      borderRadius: 2,
                      bgcolor: "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                        px: 1.5,
                        py: 1.25,
                        bgcolor:
                          resumoDeteccoesAberto === "forte"
                            ? palette.emeraldSoft
                            : palette.warningSoft,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: palette.text, fontWeight: 700 }}
                        >
                          {rotuloContagemDeteccao(
                            itensPorGrupo[resumoDeteccoesAberto].length,
                            resumoDeteccoesAberto
                          )}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: palette.textMuted }}
                        >
                          {t(
                            resumoDeteccoesAberto === "forte"
                              ? "interview_detection_strong_help"
                              : "interview_detection_review_help"
                          )}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        onClick={() => setResumoDeteccoesAberto(null)}
                      >
                        {t("btn_close")}
                      </Button>
                    </Box>
                    <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
                      {itensPorGrupo[resumoDeteccoesAberto].map((item) => (
                        <Button
                          key={item.ref}
                          fullWidth
                          onClick={() =>
                            navegarParaRegra(item.skillRef, item.ref)
                          }
                          aria-label={`${t("interview_open_rule")} ${
                            item.question
                          }`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1.5,
                            px: 1.5,
                            py: 1.25,
                            borderTop: `1px solid ${palette.border}`,
                            borderRadius: 0,
                            textAlign: "left",
                            textTransform: "none",
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: palette.text, fontWeight: 600 }}
                            >
                              {item.question}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: palette.textMuted }}
                            >
                              Item {item.numero} · {item.skillName} · {item.deteccao.motivo}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={ROTULOS[item.deteccao.nivel]}
                            variant="outlined"
                            sx={{
                              flexShrink: 0,
                              color: COR_NIVEL[item.deteccao.nivel],
                              borderColor: COR_NIVEL[item.deteccao.nivel],
                            }}
                          />
                        </Button>
                      ))}
                    </Box>
                  </Paper>
                )}

                <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 1.5 }}>
                  {state.draft.checklist.skills.length === 0 && (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: "center",
                        border: `1px dashed ${palette.border}`,
                        borderRadius: 2,
                        bgcolor: palette.background,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: palette.textMuted, mb: 1.5 }}
                      >
                        {t("interview_empty_rules")}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={busy || aplicada}
                        onClick={adicionarSkill}
                      >
                        {t("interview_add_skill")}
                      </Button>
                    </Box>
                  )}

                  {state.draft.checklist.skills.map((skill) => {
                    const itensDaSecao = itensPorSecao[skill.ref] || [];
                    const revisar = itensDaSecao.reduce(
                      (total, item) =>
                        total + (item.deteccao.nivel === "forte" ? 0 : 1),
                      0
                    );
                    // A visão inicial é o índice: uma seção só abre quando o
                    // professor a escolhe ou navega por um sinal da IA.
                    const aberta =
                      secoesRegrasAbertas[skill.ref] ??
                      skill.items.length === 0;
                    const primeiroItemDoStatus =
                      (revisar > 0
                        ? itensDaSecao.find(
                            (item) => item.deteccao.nivel !== "forte"
                          )
                        : itensDaSecao[0]) || null;

                    return (
                      <Paper
                        key={skill.ref}
                        elevation={0}
                        sx={{
                          mb: 2.5,
                          overflow: "hidden",
                          borderRadius: 2.5,
                          border: `1px solid ${palette.border}`,
                          bgcolor: palette.background,
                        }}
                      >
                        <CabecalhoSecaoRegras
                          skill={skill} busy={busy || aplicada} aberta={aberta}
                          idConteudo={`interview-rule-section-${skill.ref}`}
                          onAlternar={() => setSecoesRegrasAbertas((atuais) => ({ ...atuais, [skill.ref]: !aberta }))}
                          onEditar={(secao, campo, valor) => editarSkill(secao, campo, valor, t(campo === "name" ? "interview_field_name" : "interview_field_points"))}
                          rotuloStatus={primeiroItemDoStatus ? rotuloContagemDeteccao(revisar === 0 ? skill.items.length : revisar, revisar === 0 ? "forte" : "revisar") : undefined}
                          revisar={revisar > 0}
                          onAbrirStatus={primeiroItemDoStatus ? () => navegarParaRegra(primeiroItemDoStatus.skillRef, primeiroItemDoStatus.ref) : undefined}
                          onAdicionarItem={() => adicionarItem(skill)}
                          onRemover={() => removerSkill(skill)}
                        />
                        <Collapse
                          in={aberta}
                          timeout={200}
                          unmountOnExit
                          id={`interview-rule-section-${skill.ref}`}
                        >
                          <Divider />
                          <Box sx={{ p: 1.5 }}>
                            {skill.items.length === 0 && (
                              <Box
                                sx={{
                                  p: 2,
                                  textAlign: "center",
                                  color: palette.textMuted,
                                }}
                              >
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  {t("interview_empty_section_rules")}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={busy || aplicada}
                                  onClick={() => adicionarItem(skill)}
                                >
                                  {t("interview_add_item")}
                                </Button>
                              </Box>
                            )}

                            {skill.items.map((item) => (
                              <Box
                                key={item.ref}
                                id={`interview-rule-item-${item.ref}`}
                                role="region"
                                aria-label={item.question}
                                tabIndex={-1}
                                sx={{
                                  scrollMarginTop: 16,
                                  "& > .MuiPaper-root":
                                    itemRegraDestacado === item.ref
                                      ? {
                                          borderColor: palette.brand,
                                          boxShadow: `0 0 0 3px ${palette.brandSoft}`,
                                        }
                                      : undefined,
                                }}
                              >
                                <RegrasDoItem
                                  item={item}
                                  numero={numeroPorItemRef[item.ref]}
                                  busy={busy || aplicada}
                                  sugestaoStatus={!camera2Aprofundar && itemRefAprofundar === item.ref && (aprofundando || sincronizandoGpu)
                                    ? "carregando"
                                    : !aprofundando && !sincronizandoGpu && state.enrichment?.focus === `ITEM_RULES:${item.ref}`
                                    ? "pronta"
                                    : null}
                                  onEditarItem={(atual, campo, valor) =>
                                    editarItem(
                                      atual,
                                      campo,
                                      valor,
                                      campo === "question"
                                        ? t("interview_field_question")
                                        : t("interview_field_value")
                                    )
                                  }
                                  onAlternarCritico={alternarCritico}
                                  onRemover={removerItem}
                                  onEditarFaceta={editarFaceta}
                                  onEditarPassos={editarPassos}
                                  onPedirAoAssistente={pedirRegrasAoAssistente}
                                />
                              </Box>
                            ))}
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}
                </Box>
                <Box
                  sx={{
                    p: 2,
                    pr: vaoCopiloto,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    borderTop: `1px solid ${palette.border}`,
                  }}
                >
                  <Button onClick={() => abrirAba(1)}>
                    {t("interview_wizard_back")}
                  </Button>
                </Box>
              </Box>
            )}

            {aba === 4 && !abasDoChecklistTrancadas && (
              <AbaRegrasCamera2
                enabled={state.draft.checklist.camera2_enabled === true}
                onToggle={state.scenario_reviewed ? alternarCamera2 : undefined}
                skills={state.draft.checklist.skills}
                busy={busy || aplicada || state.status !== "aberta"}
                versaoCampos={versaoCamposCamera2}
                onEditar={editarFacetaCamera2}
                onPedirEnquadramento={aplicada ? undefined : pedirEnquadramentoAoAssistente}
                statusEnquadramento={enquadramentoAprofundar && (aprofundando || sincronizandoGpu)
                  ? "carregando" : !aprofundando && !sincronizandoGpu && state.enrichment?.focus === FOCO_ENQUADRAMENTO ? "pronta" : null}
                secoesAbertas={secoesRegrasCamera2Abertas}
                onAlterarSecoes={setSecoesRegrasCamera2Abertas}
                onEditarSecao={(skill, campo, valor) => editarSkill(skill, campo, valor, t(campo === "name" ? "interview_field_name" : "interview_field_points"))}
                onAdicionarItem={adicionarItem}
                onAdicionarSecao={aplicada ? undefined : adicionarSkill}
                onRemoverSecao={removerSkill}
                guidance={state.draft.checklist.camera2_guidance}
                onEditarGuidance={editarGuidanceCamera2}
                onEditarPassos={editarPassosCamera2}
                onEditarItem={(item, campo, valor) => editarItem(item, campo, valor, t(campo === "question" ? "interview_field_question" : "interview_field_value"))}
                onAlternarCritico={alternarCritico}
                onRemover={removerItem}
                onPedirRegras={(item) => pedirRegrasAoAssistente(item, true)}
                statusDoItem={(item) => camera2Aprofundar && itemRefAprofundar === item.ref && (aprofundando || sincronizandoGpu)
                  ? "carregando" : !aprofundando && !sincronizandoGpu && state.enrichment?.focus === focoRegras(item.ref, true) ? "pronta" : null}
              />
            )}

            {/* ── aba 3 · conversa completa com o assistente ──────────────── */}
            {aba === 3 && (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                }}
              >
                {estreito && (
                  <PainelLacunas
                    compacto
                    gaps={gapsDaFase}
                    waivers={state.draft.waivers}
                    selecionada={emFoco}
                    busy={busy}
                    aplicada={aplicada}
                    onSelecionar={focarLacuna}
                    onIrParaCampo={irParaCampo}
                    onPedirAjuda={pedirAjuda}
                    onDispensar={dispensar}
                    onRevogar={revogar}
                  />
                )}

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <ConversationPane
                    messages={mensagens}
                    hasAttachments={importacao.files.length > 0}
                    inputStart={!aplicada ? <AnexarArquivos state={state} control={importacao} disabled={busy} /> : undefined}
                    value={input}
                    onChange={setInput}
                    onSend={enviar}
                    busy={busy}
                    busyLabel={importacao.uploading ? "Lendo arquivos" : t(
                      gerando
                        ? state.scenario_reviewed
                          ? "interview_generate_checklist_waiting"
                          : "interview_generate_waiting"
                        : "interview_thinking"
                    )}
                    placeholder={
                      (aplicada
                        ? t("interview_closed_placeholder")
                        : t("interview_placeholder")) ?? undefined
                    }
                    multiline
                    beforeInput={
                      aplicada ? undefined : (
                        <>
                          <ImportacaoDocumentos state={state} control={importacao} disabled={busy} />
                          <Box
                            sx={{
                              px: 2,
                              pt: 0.5,
                              pb: 1,
                              display: "flex",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            {(!state.scenario_reviewed || checklistVazio || state.degraded || Boolean(erro)) && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={busy || gerando || importacaoAguardaLeitura(state)}
                                startIcon={
                                  <AutoAwesomeOutlinedIcon fontSize="small" />
                                }
                                onClick={() =>
                                  state.scenario_reviewed
                                    ? void gerarChecklist()
                                    : gerarCenarioDoContexto()
                                }
                              >
                                {t(
                                  state.scenario_reviewed
                                    ? "interview_generate_checklist"
                                    : "interview_generate"
                                )}
                              </Button>
                            )}

                            <Tooltip title={renderTextoComNegrito(temRascunho ? t("interview_enrich_hint") : t("interview_enrich_needs_draft"))}>
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={bloquearRevisaoMedica}
                                  startIcon={<LocalHospitalOutlinedIcon fontSize="small" />}
                                  onClick={abrirAprofundar}
                                >
                                  {t("interview_enrich")}
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>
                          {emFoco && (
                            <RespostaGuiada
                              forma={emFoco.forma}
                              busy={busy}
                              onResponder={responderGuiado}
                            />
                          )}
                        </>
                      )
                    }
                  />
                </Box>

                {!estreito && (
                  <PainelLacunas
                    gaps={gapsDaFase}
                    waivers={state.draft.waivers}
                    selecionada={emFoco}
                    busy={busy}
                    aplicada={aplicada}
                    onSelecionar={focarLacuna}
                    onIrParaCampo={irParaCampo}
                    onPedirAjuda={pedirAjuda}
                    onDispensar={dispensar}
                    onRevogar={revogar}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* A camada. Só na espera LONGA: a curta não justifica cobrir a
              tela. Título e detalhe são as MESMAS frases do Copiloto, para a
              página não contar duas versões do que está acontecendo. */}
          <SobreposicaoTrabalho
            ativo={escrevendo}
            titulo={tituloEspera}
            detalhe={detalheEspera}
          />
        </Box>
      </Paper>

      {/* ── COPILOTO IA LATERAL / FLUTUANTE ESTILO JIRA ROVO ────────────── */}
      {aba !== 3 && (
        <Paper
          elevation={8}
          ref={dockCopiloto}
          sx={{
            position: "fixed",
            bottom: !copilotoAberto && estreito ? 88 : 24,
            right: 28,
            width: copilotoAberto
              ? { xs: "calc(100vw - 32px)", sm: 420 }
              : estreito
              ? 48
              : "auto",
            height: copilotoAberto ? 520 : 48,
            maxHeight: copilotoAberto
              ? estreito
                ? "calc(100dvh - 96px)"
                : "calc(100dvh - 250px)"
              : 48,
            display: "flex",
            flexDirection: "column",
            borderRadius: 3,
            overflow: "hidden",
            zIndex: 1200,
            border: `1px solid ${palette.border}`,
            bgcolor: palette.surface,
            boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
            transition: "all 0.25s ease-in-out",
          }}
        >
          {/* Header do Widget Copiloto */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent:
                copilotoAberto || !estreito ? "space-between" : "center",
              px: !copilotoAberto && estreito ? 0.75 : 2,
              py: 1,
              bgcolor: palette.brandDark,
              color: "#fff",
              cursor: "pointer",
            }}
            onClick={() => setCopilotoAberto(!copilotoAberto)}
          >
            {(copilotoAberto || !estreito) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AutoAwesomeOutlinedIcon
                  fontSize="small"
                  sx={{ color: palette.brand }}
                />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: "#fff" }}
                >
                  {t("interview_copilot_title")}
                </Typography>
              </Box>
            )}
            {/* Minimizado, o dock só oferece UMA coisa: abrir o Copiloto.
                Antes ele carregava também «Completar cenário com IA» — uma
                ação de gerar o cenário inteiro disparada de uma barrinha de
                48px, colada no botão de abrir e sem o chat à vista para
                explicar o que ela ia sobrescrever. A ação continua onde tem
                contexto: dentro do Copiloto aberto e na aba Conversa. */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Button
                size="small"
                sx={{
                  color: "#fff",
                  minWidth: "auto",
                  px: 0.75,
                  fontSize: 11,
                  textTransform: "none",
                }}
                startIcon={
                  copilotoAberto ? (
                    <KeyboardArrowDownIcon fontSize="small" />
                  ) : (
                    <ChatOutlinedIcon fontSize="small" />
                  )
                }
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  event.stopPropagation();
                  setCopilotoAberto(!copilotoAberto);
                }}
                aria-label={t(copilotoAberto ? "interview_copilot_minimize" : "interview_copilot_expand") ?? ""}
              >
                {copilotoAberto ? t("interview_copilot_minimize") : null}
              </Button>
            </Box>
          </Box>

          {/* Corpo do Copiloto (Chat & Briefing) */}
          {copilotoAberto && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {aplicada && (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: palette.brandSoft,
                    borderBottom: `1px solid ${palette.border}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: palette.brandDark, display: "block", mb: 1 }}
                  >
                    {t("interview_copilot_closed_hint")}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      disabled={busy}
                      onClick={reabrir}
                    >
                      {t("interview_reopen")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      disabled={busy}
                      onClick={duplicarEntrevista}
                    >
                      {t("interview_duplicate")}
                    </Button>
                  </Box>
                </Box>
              )}
              <ConversationPane
                messages={mensagens}
                hasAttachments={importacao.files.length > 0}
                inputStart={!aplicada ? <AnexarArquivos state={state} control={importacao} disabled={busy} /> : undefined}
                value={input}
                onChange={setInput}
                onSend={enviar}
                busy={busy}
                busyLabel={importacao.uploading ? "Lendo arquivos" : t(
                  gerando
                    ? state.scenario_reviewed
                      ? "interview_generate_checklist_waiting"
                      : "interview_generate_waiting"
                    : "interview_thinking"
                )}
                placeholder={
                  aplicada
                    ? t("interview_closed_placeholder") ?? undefined
                    : t("interview_placeholder") ??
                      "Peça alterações no cenário ou checklist..."
                }
                multiline
                beforeInput={
                  !aplicada ? (
                    <>
                    <ImportacaoDocumentos state={state} control={importacao} disabled={busy} />
                    {/* O atalho de geração aparece quando a aba não o oferece. */}
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        display: "flex",
                        gap: 0.75,
                        flexWrap: "wrap",
                        alignItems: "center",
                        borderTop: `1px solid ${palette.border}`,
                      }}
                    >
                      {(!state.scenario_reviewed || checklistVazio || state.degraded || Boolean(erro)) &&
                        !acaoDeGerarVisivelNaAba && (
                          <Chip
                            size="small"
                            variant="outlined"
                            clickable
                            disabled={busy || gerando || importacaoAguardaLeitura(state)}
                            icon={
                              <AutoAwesomeOutlinedIcon sx={{ fontSize: 15 }} />
                            }
                            label={t(
                              state.scenario_reviewed
                                ? "interview_generate_checklist"
                                : "interview_generate"
                            )}
                            onClick={() =>
                              state.scenario_reviewed
                                ? void gerarChecklist()
                                : gerarCenarioDoContexto()
                            }
                          />
                        )}
                      <Tooltip title={renderTextoComNegrito(temRascunho ? t("interview_enrich_hint") : t("interview_enrich_needs_draft"))}>
                        <span>
                          <Chip
                            size="small"
                            variant="outlined"
                            clickable
                            disabled={bloquearRevisaoMedica}
                            icon={<LocalHospitalOutlinedIcon sx={{ fontSize: 15 }} />}
                            label={t("interview_enrich")}
                            onClick={abrirAprofundar}
                          />
                        </span>
                      </Tooltip>
                    </Box>
                    </>
                  ) : undefined
                }
              />
            </Box>
          )}
        </Paper>
      )}

      {/* Botão de Conclusão / Aplicação no rodapé */}
      {!aplicada && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mt: 1,
            pr: vaoCopiloto,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={aplicar}
            disabled={
              applying ||
              ocupado ||
              importacaoPendente(state) ||
              !state.scenario_reviewed ||
              bloqueantes.length > 0
            }
            startIcon={
              applying ? (
                <CircularProgress size={14} thickness={6} />
              ) : undefined
            }
          >
            {applying
              ? t("interview_applying")
              : t("interview_btn_apply_finish")}
          </Button>
          <Typography variant="caption" sx={{ color: palette.textMuted }}>
            {aguardandoConfirmacao
              ? t("interview_apply_waiting_scenario_review")
              : checklistVazio && state.scenario_reviewed
              ? t("interview_apply_waiting_checklist")
              : bloqueantes.length > 0
              ? `${t("interview_apply_blocked")} ${bloqueantes.length} ${t(
                  bloqueantes.length === 1
                    ? "interview_pending_required_one"
                    : "interview_pending_required_many"
                )}. ${t("interview_apply_blocked_hint")}`
              : t("interview_apply_hint")}
          </Typography>
        </Box>
      )}

      <PainelAprofundar
        aberto={painelAprofundar}
        proposta={enquadramentoAprofundar
          ? state.enrichment?.focus === FOCO_ENQUADRAMENTO ? state.enrichment : null
          : itemRefAprofundar
          ? state.enrichment?.focus === focoRegras(itemRefAprofundar, camera2Aprofundar) ? state.enrichment : null
          : ehFocoRegras(state.enrichment?.focus) || state.enrichment?.focus === FOCO_ENQUADRAMENTO ? null : state.enrichment}
        itemEmRevisao={itemNomeAprofundar}
        cameraFocal={camera2Aprofundar && (itemRefAprofundar !== null || enquadramentoAprofundar)}
        enquadramentoFocal={enquadramentoAprofundar}
        carregando={aprofundando}
        sincronizando={sincronizandoGpu}
        adotando={adotando}
        resumoPayload={resumoDoRascunho(state)}
        aoFechar={() => setPainelAprofundar(false)}
        aoPedir={aprofundar}
        aoAdotar={adotarEmendas}
        aoDescartar={descartarAprofundamento}
        erro={erro}
      />
    </Box>
  );
}
